import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@/lib/i18n-locales";

type Messages = Record<string, unknown>;
export type LandingI18nContext = {
  locale: SupportedLocale;
  messages: Messages;
};

const navbarTemplatesLabel = {
  en: "Templates",
  pt: "Templates",
  es: "Plantillas",
} as const;

function withRuntimeMessageFallbacks(
  locale: string,
  messages: Messages,
): Messages {
  const navbar =
    typeof messages.Navbar === "object" && messages.Navbar !== null
      ? (messages.Navbar as Record<string, unknown>)
      : {};
  const templatesLabel =
    navbarTemplatesLabel[locale as keyof typeof navbarTemplatesLabel] ??
    "Templates";
  return {
    ...messages,
    Navbar: {
      ...navbar,
      templates: navbar.templates ?? templatesLabel,
    },
  };
}

// Import only the visitor's locale. Each translation catalog becomes an
// independent Vite chunk instead of adding all seven (~650 KB JSON) to the
// entry bundle. SSR loads the same catalog through the Bun server bundle.
type LocaleImporter = () => Promise<{ default: unknown }>;

const localeImporters: Record<SupportedLocale, LocaleImporter> = {
  de: () => import("@sendbot/i18n/messages/de"),
  en: () => import("@sendbot/i18n/messages/en"),
  es: () => import("@sendbot/i18n/messages/es"),
  fr: () => import("@sendbot/i18n/messages/fr"),
  it: () => import("@sendbot/i18n/messages/it"),
  pt: () => import("@sendbot/i18n/messages/pt"),
  ro: () => import("@sendbot/i18n/messages/ro"),
};

const resolvedMessages = new Map<SupportedLocale, Messages>();
const loadingMessages = new Map<SupportedLocale, Promise<Messages>>();

let clientContext: LandingI18nContext | undefined;

export function normalizeLocale(value?: string | null): SupportedLocale {
  return SUPPORTED_LOCALES.includes(value as SupportedLocale)
    ? (value as SupportedLocale)
    : DEFAULT_LOCALE;
}

export async function getMessagesForLocale(locale: string): Promise<Messages> {
  const normalized = normalizeLocale(locale);
  const cached = resolvedMessages.get(normalized);
  if (cached) return cached;

  const pending = loadingMessages.get(normalized);
  if (pending) return pending;

  const loading = localeImporters[normalized]()
    .then((module) => {
      const messages = withRuntimeMessageFallbacks(
        normalized,
        module.default as Messages,
      );
      resolvedMessages.set(normalized, messages);
      loadingMessages.delete(normalized);
      return messages;
    })
    .catch((error: unknown) => {
      loadingMessages.delete(normalized);
      throw error;
    });

  loadingMessages.set(normalized, loading);
  return loading;
}

export function setClientI18nContext(context: LandingI18nContext) {
  clientContext = context;
}

export function getClientI18nContext(): LandingI18nContext {
  if (clientContext) return clientContext;
  const locale = normalizeLocale(
    typeof document === "undefined"
      ? DEFAULT_LOCALE
      : document.documentElement.dataset.locale ||
          document.documentElement.lang.split("-")[0],
  );
  return { locale, messages: resolvedMessages.get(locale) ?? {} };
}

function lookup(messages: Messages, key: string): unknown {
  return key.split(".").reduce<unknown>((value, segment) => {
    if (!value || typeof value !== "object" || Array.isArray(value))
      return undefined;
    return (value as Record<string, unknown>)[segment];
  }, messages);
}

function interpolate(value: string, values?: Record<string, unknown>): string {
  if (!values) return value;
  return value.replace(/\{([\w.-]+)\}/g, (match, key) =>
    values[key] === undefined || values[key] === null
      ? match
      : String(values[key]),
  );
}

export function createTranslator(messages: Messages, namespace?: string) {
  return (key: string, values?: Record<string, unknown>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key;
    const value = lookup(messages, fullKey);
    if (typeof value === "string") return interpolate(value, values);
    if (value === undefined) return fullKey;
    return String(value);
  };
}
